import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/** Évènement non standard `beforeinstallprompt` (Chromium / Edge / Android). */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export type InstallOutcome = 'accepted' | 'dismissed' | 'unavailable';

/**
 * Détecte un appareil iOS (iPhone/iPad/iPod), y compris iPadOS 13+ qui se
 * présente comme un Mac mais avec un écran tactile.
 */
export function isIosDevice(userAgent: string, maxTouchPoints: number): boolean {
  if (/iPhone|iPad|iPod/i.test(userAgent)) {
    return true;
  }
  return /Macintosh/.test(userAgent) && maxTouchPoints > 1;
}

/**
 * iOS ne déclenche pas `beforeinstallprompt` : on propose l'ajout manuel à
 * l'écran d'accueil, sauf si l'app tourne déjà en mode standalone (déjà installée).
 */
export function shouldOfferIosInstall(
  userAgent: string,
  maxTouchPoints: number,
  isStandalone: boolean,
): boolean {
  return isIosDevice(userAgent, maxTouchPoints) && !isStandalone;
}

/** Heuristique « appareil mobile » — sert à n'afficher la bannière d'install que sur mobile. */
export function isMobileUa(userAgent: string): boolean {
  return /Android|iPhone|iPad|iPod|Mobi/i.test(userAgent);
}

/**
 * Capture l'évènement `beforeinstallprompt` pour proposer l'installation de la
 * PWA au moment voulu (Android / desktop). iOS ne supporte pas cet évènement :
 * l'installation y reste manuelle (« Sur l'écran d'accueil »).
 */
@Injectable({ providedIn: 'root' })
export class PwaInstallService {
  private static readonly DISMISS_KEY = 'pwa-install-dismissed';
  private deferredPrompt: BeforeInstallPromptEvent | null = null;
  private enabled = false;
  private readonly _installable$ = new BehaviorSubject<boolean>(false);

  /** Émet `true` lorsqu'une invite d'installation est disponible. */
  readonly installable$: Observable<boolean> = this._installable$.asObservable();

  /**
   * Vrai si l'on doit proposer l'installation manuelle iOS (« Sur l'écran
   * d'accueil »), faute d'API d'installation native sur iOS.
   */
  get isIosInstallAvailable(): boolean {
    if (!this.enabled) {
      return false;
    }
    const standalone =
      (navigator as { standalone?: boolean }).standalone === true ||
      window.matchMedia?.('(display-mode: standalone)')?.matches === true;
    return shouldOfferIosInstall(
      navigator.userAgent,
      navigator.maxTouchPoints,
      standalone,
    );
  }

  /** Vrai sur un appareil mobile (utilisé pour limiter la bannière d'install à Android). */
  get isMobile(): boolean {
    return isMobileUa(navigator.userAgent);
  }

  init(): void {
    this.enabled = true;
    window.addEventListener('beforeinstallprompt', event => {
      event.preventDefault();
      this.deferredPrompt = event as BeforeInstallPromptEvent;
      this._installable$.next(true);
    });
    window.addEventListener('appinstalled', () => {
      this.dismissInstall();
      this.reset();
    });
  }

  /** Mémorise que l'utilisateur a refusé (ou installé) → on ne réaffiche plus l'invite. */
  dismissInstall(): void {
    localStorage.setItem(PwaInstallService.DISMISS_KEY, '1');
  }

  /** Vrai si l'invite a déjà été refusée/installée précédemment. */
  get installInviteDismissed(): boolean {
    return localStorage.getItem(PwaInstallService.DISMISS_KEY) === '1';
  }

  /** Affiche l'invite native si disponible et retourne le choix de l'utilisateur. */
  async promptInstall(): Promise<InstallOutcome> {
    if (!this.deferredPrompt) {
      return 'unavailable';
    }
    const prompt = this.deferredPrompt;
    await prompt.prompt();
    const choice = await prompt.userChoice;
    this.reset();
    return choice.outcome;
  }

  private reset(): void {
    this.deferredPrompt = null;
    this._installable$.next(false);
  }
}
